import type { NatsConnection } from "nats.ws";
import { JSONCodec, NatsError, nuid } from "nats.ws";
import type { ZodType } from "zod";

import { ApiError, TimeoutError } from "./error";
import type { CallContext, CallContextFactory } from "./request";
import { natsHeaders } from "./request";
import { handleResponse } from "./response";
import { camelToSnake, snakeToCamel } from "./transform";

export interface NatsCaller {
  request<T>(
    subject: string,
    action: string,
    payload: object,
    schema: ZodType<T>,
    timeout?: number,
  ): Promise<T>;
}

export class DefaultNatsCaller implements NatsCaller {
  codec = JSONCodec();

  constructor(
    private connection: NatsConnection,
    private company: string,
    private userId: string,
    private contextFactory: CallContextFactory,
  ) {}

  async request<T>(
    subject: string,
    action: string,
    payload: object,
    schema: ZodType<T>,
    timeout?: number,
  ): Promise<T> {
    subject = subject.replace("${company_id}", this.company).replace("${user_id}", this.userId);

    // Transform camelCase payload to snake_case before sending to backend
    const snakeCasePayload = camelToSnake(payload);
    const context = this.contextFactory(subject, action, snakeCasePayload, timeout);

    try {
      const rawResponse = await this.connection.request(
        subject,
        this.codec.encode(snakeCasePayload),
        {
          timeout: context.requestTimeout,
          reply: `_INBOX.${this.userId}.${context.action}.${nuid.next()}`,
          noMux: true,
          headers: natsHeaders(context),
        },
      );
      const response = this.codec.decode(rawResponse.data);
      const data = handleResponse(response, context);
      // Transform snake_case data from backend to camelCase before Zod validation
      const camelCaseData = snakeToCamel(data);
      return schema.parse(camelCaseData);
    } catch (err: unknown) {
      handleError(err, context);
    }
  }
}

function handleError(err: unknown, context: CallContext): never {
  if (err instanceof NatsError) {
    if (err.code === "TIMEOUT") {
      throw new TimeoutError({
        request: context,
        response: {},
      });
    }
    throw new ApiError(err.name, `Nats Error while calling RPC method ${context.action}`, {
      request: context,
      response: {
        message: err.message,
        code: err.code,
        cause: err.chainedError,
      },
    });
  }
  throw err;
}
